import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

/**
 * Shared hook for resolving the current user's team_id.
 * Queries team_members exactly ONCE per session (cached via React Query).
 * Applies sole-proprietor fallback: effectiveTeamId = teamId ?? user.id
 */
export function useTeamId() {
  const { user } = useAuth();

  const { data, isLoading } = useQuery({
    queryKey: ['team-id', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;

      const { data: memberRow, error } = await supabase
        .from('team_members')
        .select('team_id')
        .eq('user_id', user.id)
        .eq('status', 'active')
        .limit(1)
        .maybeSingle();

      if (error) {
        console.warn('[useTeamId] Query error:', error.message);
      }

      return memberRow?.team_id ?? null;
    },
    enabled: !!user?.id,
    staleTime: Infinity, // Team ID doesn't change during a session
    gcTime: 30 * 60 * 1000, // Keep in cache for 30 minutes
  });

  const teamId = data ?? null;
  const effectiveTeamId = teamId ?? user?.id ?? '';

  return {
    teamId,
    effectiveTeamId,
    loading: isLoading,
  };
}
